/**
 * Entity Details Dialog Component
 * Shows detailed information about an entity. For website nodes the dialog
 * uses a tabbed layout (Overview / URLs / Devices / Notes) so the full list
 * of visited URLs gets a proper "blow up" pane instead of being squeezed
 * into a 280-px sidebar. Other entity types (device / location / generic)
 * fall back to a simpler Overview + Notes layout.
 *
 * Layout was modeled after the Geofence Activity History dialog in COP for
 * a consistent "drill-down detail" feel across the app.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Tooltip,
  Button,
  Link as MuiLink,
  useTheme,
  List,
  Avatar,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
  Language as BrowseIcon,
  Apps as AppsIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Flag as FlagIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Devices as DevicesIcon,
  ListAlt as ListAltIcon,
  Dashboard as OverviewIcon,
  ManageSearch as SearchTermIcon,
} from '@mui/icons-material';
import {
  Note,
  EntityType,
  getNotesForEntity,
  deleteNote,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  NotePriority,
} from '../../services/notesApi';
import deviceDataService from '../../services/deviceDataService';
import {
  ExtractedSearch,
  aggregateSearches,
  tokenizeKeywords,
} from '../../utils/searchTermExtraction';

interface EntityStats {
  totalConnections: number;
  callConnections: number;
  smsConnections: number;
  browsingConnections: number;
  appConnections?: number;
  dwellConnections: number;
  connectedDevices: string[];
  connectedLocations: string[];
  firstSeen?: string;
  lastSeen?: string;
}

interface DomainUrl {
  url: string;
  count: number;
  unique_visitors?: number;
  last_visit?: string;
}

interface EntityDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  entityLabel?: string;
  // Stats calculated from graph data
  stats?: EntityStats;
  websiteDetails?: {
    domain: string;
    currentHost?: string;
    sampleUrl?: string;
    hosts: string[];
    hasOtherSubdomainsInView?: boolean;
  };
  // Date range for fetching URLs (optional)
  dateRange?: { start: Date | null; end: Date | null };
  // Callback to open the note creation dialog
  onAddNote?: () => void;
  onEditNote?: (note: Note) => void;
}

type UrlSortKey = 'visits' | 'recent' | 'unique';
type SearchSortKey = 'count' | 'recent' | 'alpha';

const EntityDetailsDialog: React.FC<EntityDetailsDialogProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityLabel,
  stats,
  websiteDetails,
  dateRange,
  onAddNote,
  onEditNote,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Domain URLs state (fetched from backend for website nodes)
  const [domainUrls, setDomainUrls] = useState<DomainUrl[]>([]);
  const [urlsLoading, setUrlsLoading] = useState(false);
  const [urlsError, setUrlsError] = useState<string | null>(null);
  const [urlSearch, setUrlSearch] = useState('');
  const [urlSort, setUrlSort] = useState<UrlSortKey>('visits');

  // Search-term extraction state (Searches tab)
  const [searchTermFilter, setSearchTermFilter] = useState('');
  const [searchSort, setSearchSort] = useState<SearchSortKey>('count');

  // Active tab (Overview by default). Tab visibility depends on whether this
  // is a website node -- non-website entities skip URLs/Devices/Searches tabs.
  const [tab, setTab] = useState<'overview' | 'urls' | 'searches' | 'devices' | 'notes'>('overview');

  // Reset tab to overview whenever the dialog opens for a different entity so
  // we don't strand the user on a tab that doesn't exist for the new target.
  useEffect(() => {
    if (open) setTab('overview');
  }, [open, entityId]);

  useEffect(() => {
    if (open && entityType && entityId) {
      loadNotes();
    }
  }, [open, entityType, entityId]);

  useEffect(() => {
    if (open && websiteDetails?.domain) {
      loadDomainUrls();
    } else {
      setDomainUrls([]);
      setUrlsError(null);
    }
  }, [open, websiteDetails?.domain, dateRange?.start, dateRange?.end]);

  const loadDomainUrls = async () => {
    if (!websiteDetails?.domain) return;

    setUrlsLoading(true);
    setUrlsError(null);
    try {
      const startDate = dateRange?.start ? dateRange.start.toISOString().split('T')[0] : undefined;
      const endDate = dateRange?.end ? dateRange.end.toISOString().split('T')[0] : undefined;

      const response = await deviceDataService.getDomainUrls(
        websiteDetails.domain,
        startDate,
        endDate,
        undefined,
        500
      );

      setDomainUrls(response.urls || []);
    } catch (err: any) {
      console.error('Error loading domain URLs:', err);
      setUrlsError('Failed to load URLs');
      setDomainUrls([]);
    } finally {
      setUrlsLoading(false);
    }
  };

  const loadNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedNotes = await getNotesForEntity(entityType, entityId);
      setNotes(fetchedNotes);
    } catch (err: any) {
      console.error('Error loading notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Filter + sort URLs (client-side; the list is capped at 500 from the API
  // so this is cheap even with the largest payloads).
  const filteredUrls = useMemo(() => {
    const searchTerm = urlSearch.trim().toLowerCase();
    let list = searchTerm
      ? domainUrls.filter((u) => u.url.toLowerCase().includes(searchTerm))
      : [...domainUrls];

    list.sort((a, b) => {
      if (urlSort === 'visits') return b.count - a.count;
      if (urlSort === 'unique') return (b.unique_visitors || 0) - (a.unique_visitors || 0);
      // 'recent'
      const aT = a.last_visit ? new Date(a.last_visit).getTime() : 0;
      const bT = b.last_visit ? new Date(b.last_visit).getTime() : 0;
      return bT - aT;
    });
    return list;
  }, [domainUrls, urlSearch, urlSort]);

  // Aggregate "blow-up" header for the URLs tab.
  const urlAggregates = useMemo(() => {
    const totalVisits = domainUrls.reduce((sum, u) => sum + (u.count || 0), 0);
    const lastVisitTimes = domainUrls
      .map((u) => (u.last_visit ? new Date(u.last_visit).getTime() : 0))
      .filter((t) => t > 0);
    const mostRecent = lastVisitTimes.length ? new Date(Math.max(...lastVisitTimes)) : null;
    return {
      uniqueUrls: domainUrls.length,
      totalVisits,
      mostRecent,
    };
  }, [domainUrls]);

  // Run the search-term extractor over every visited URL once and group the
  // results by `engine|term` so repeat searches collapse into a single row
  // with a count. Aggregating client-side keeps the feature self-contained
  // (no new backend endpoint) and is plenty fast for the 500-URL API cap.
  const searchExtractions = useMemo<ExtractedSearch[]>(
    () =>
      aggregateSearches(
        domainUrls.map((u) => ({ url: u.url, count: u.count, lastVisit: u.last_visit })),
      ),
    [domainUrls],
  );

  // Aggregate stats + per-engine breakdown for the Searches tab header.
  const searchAggregates = useMemo(() => {
    const totalSearches = searchExtractions.reduce((sum, s) => sum + s.count, 0);
    const engineCounts = new Map<string, { count: number; color: string }>();
    for (const s of searchExtractions) {
      const ex = engineCounts.get(s.engine);
      if (ex) ex.count += s.count;
      else engineCounts.set(s.engine, { count: s.count, color: s.engineColor });
    }
    const lastSearchMs = searchExtractions.reduce((m, s) => Math.max(m, s.lastVisit), 0);
    return {
      uniqueTerms: searchExtractions.length,
      totalSearches,
      lastSearch: lastSearchMs > 0 ? new Date(lastSearchMs) : null,
      engines: Array.from(engineCounts.entries())
        .map(([name, v]) => ({ name, count: v.count, color: v.color }))
        .sort((a, b) => b.count - a.count),
    };
  }, [searchExtractions]);

  // Top keywords across all search terms, weighted by how many times each
  // term was searched. Useful for spotting recurring interests at a glance.
  const topKeywords = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of searchExtractions) {
      const tokens = tokenizeKeywords(s.term);
      // De-dupe tokens within a single term so "pizza pizza pizza" doesn't
      // dominate just because the user repeated a word in one query.
      const unique = new Set(tokens);
      for (const t of unique) {
        counts.set(t, (counts.get(t) || 0) + s.count);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([word, n]) => ({ word, count: n }));
  }, [searchExtractions]);

  // Apply Searches-tab filter + sort (post-aggregation).
  const visibleSearches = useMemo(() => {
    const term = searchTermFilter.trim().toLowerCase();
    let list = term
      ? searchExtractions.filter(
          (s) => s.term.toLowerCase().includes(term) || s.engine.toLowerCase().includes(term),
        )
      : [...searchExtractions];

    list.sort((a, b) => {
      if (searchSort === 'count') return b.count - a.count;
      if (searchSort === 'recent') return b.lastVisit - a.lastVisit;
      return a.term.localeCompare(b.term);
    });
    return list;
  }, [searchExtractions, searchTermFilter, searchSort]);

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteNote(noteId);
      setNotes(notes.filter((n) => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const handleCopyUrl = (url: string) => {
    try {
      navigator.clipboard.writeText(url);
    } catch {
      /* noop -- some browsers / iframes block clipboard access */
    }
  };

  const getEntityTypeLabel = (type: EntityType): string => {
    const labels: Record<EntityType, string> = {
      device: 'Device',
      location: 'Location',
      entity: 'Entity',
      link: 'Connection',
      geofence: 'Geofence',
    };
    return labels[type] || type;
  };

  const getEntityIcon = (type: EntityType) => {
    if (websiteDetails) return <BrowseIcon />;
    switch (type) {
      case 'device': return <PhoneIcon />;
      case 'location': return <LocationIcon />;
      default: return <InfoIcon />;
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString();

  const ensureHttps = (value: string) => {
    const v = (value || '').trim();
    if (!v) return '';
    if (v.startsWith('http://') || v.startsWith('https://')) return v;
    return `https://${v}`;
  };

  const getPriorityIcon = (priority: NotePriority) => {
    if (priority === 'critical') return <WarningIcon sx={{ color: PRIORITY_COLORS.critical, fontSize: 16 }} />;
    if (priority === 'high') return <FlagIcon sx={{ color: PRIORITY_COLORS.high, fontSize: 16 }} />;
    return null;
  };

  // ────────────────────────────────────────────────────────────────────
  // Tab content renderers (kept as nested helpers to keep the JSX lean)
  // ────────────────────────────────────────────────────────────────────

  const renderOverviewTab = () => (
    <Box sx={{ p: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: websiteDetails ? '1fr 1fr' : '1fr' }, gap: 3 }}>
      {websiteDetails && (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
            Website Identity
          </Typography>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Primary host
              </Typography>
              <MuiLink
                href={ensureHttps(websiteDetails.sampleUrl || websiteDetails.currentHost || websiteDetails.domain)}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                sx={{ fontSize: '0.95rem', wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {websiteDetails.currentHost || websiteDetails.domain}
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </MuiLink>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Root domain
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {websiteDetails.domain}
              </Typography>
            </Box>

            {websiteDetails.hosts.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Hosts under {websiteDetails.domain} ({websiteDetails.hosts.length})
                </Typography>
                <Stack spacing={0.25} sx={{ maxHeight: 150, overflow: 'auto' }}>
                  {websiteDetails.hosts.map((host) => (
                    <MuiLink
                      key={host}
                      href={ensureHttps(host)}
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                      sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}
                    >
                      {host}
                      {host === websiteDetails.domain ? ' (root)' : ''}
                      {websiteDetails.currentHost && host === websiteDetails.currentHost ? ' (current)' : ''}
                    </MuiLink>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
          Connection Statistics
        </Typography>

        {stats ? (
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                borderRadius: 2,
              }}
            >
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {stats.totalConnections}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Connections
              </Typography>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Paper elevation={0} sx={{ p: 1, bgcolor: isDarkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.08)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: '#2196f3' }} />
                  <Typography variant="body2" fontWeight="bold">{stats.callConnections}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">Calls</Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 1, bgcolor: isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.08)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SmsIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                  <Typography variant="body2" fontWeight="bold">{stats.smsConnections}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">SMS</Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 1, bgcolor: isDarkMode ? 'rgba(156, 39, 176, 0.1)' : 'rgba(156, 39, 176, 0.08)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BrowseIcon sx={{ fontSize: 14, color: '#9c27b0' }} />
                  <Typography variant="body2" fontWeight="bold">{stats.browsingConnections}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">Browse</Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 1, bgcolor: isDarkMode ? 'rgba(103, 58, 183, 0.1)' : 'rgba(103, 58, 183, 0.08)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AppsIcon sx={{ fontSize: 14, color: '#673ab7' }} />
                  <Typography variant="body2" fontWeight="bold">{stats.appConnections || 0}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">Apps</Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 1, bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.08)', borderRadius: 1, gridColumn: 'span 2' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                  <Typography variant="body2" fontWeight="bold">{stats.dwellConnections}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">Dwells</Typography>
              </Paper>
            </Box>

            {stats.connectedLocations.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Connected Locations ({stats.connectedLocations.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {stats.connectedLocations.slice(0, 6).map((l) => (
                    <Chip key={l} label={l} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                  ))}
                  {stats.connectedLocations.length > 6 && (
                    <Chip label={`+${stats.connectedLocations.length - 6}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                  )}
                </Box>
              </Box>
            )}

            {(stats.firstSeen || stats.lastSeen) && (
              <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
                {stats.firstSeen && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    First seen: {formatDate(stats.firstSeen)}
                  </Typography>
                )}
                {stats.lastSeen && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Last seen: {formatDate(stats.lastSeen)}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No statistics available
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderUrlsTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Aggregate header bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
        }}
      >
        {[
          { label: 'Unique URLs', value: urlAggregates.uniqueUrls.toLocaleString() },
          { label: 'Total Visits', value: urlAggregates.totalVisits.toLocaleString() },
          {
            label: 'Most Recent Visit',
            value: urlAggregates.mostRecent ? urlAggregates.mostRecent.toLocaleString() : '—',
          },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{
              p: 1,
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              borderRadius: 1.5,
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {label}
            </Typography>
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: value.length > 18 ? '0.85rem' : '1.05rem', lineHeight: 1.3 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Search + sort controls */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 2.5, py: 1.25, borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
        <TextField
          size="small"
          placeholder="Search URLs..."
          value={urlSearch}
          onChange={(e) => setUrlSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={urlSort}
            label="Sort by"
            onChange={(e) => setUrlSort(e.target.value as UrlSortKey)}
          >
            <MenuItem value="visits">Most Visits</MenuItem>
            <MenuItem value="recent">Most Recent</MenuItem>
            <MenuItem value="unique">Most Unique Users</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* URL list */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
        {urlsError ? (
          <Alert severity="error">{urlsError}</Alert>
        ) : urlsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : domainUrls.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography variant="body2">No URLs found for this domain</Typography>
          </Box>
        ) : filteredUrls.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography variant="body2">No URLs match the current search</Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {filteredUrls.map((u, idx) => (
              <Paper
                key={`${u.url}-${idx}`}
                elevation={0}
                sx={{
                  p: 1.25,
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  transition: 'background-color 0.15s',
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MuiLink
                    href={ensureHttps(u.url)}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{
                      fontSize: '0.85rem',
                      wordBreak: 'break-all',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      lineHeight: 1.4,
                    }}
                  >
                    {u.url}
                    <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.65 }} />
                  </MuiLink>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    <Chip
                      label={`${u.count} visit${u.count !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {u.unique_visitors && u.unique_visitors > 0 && (
                      <Chip
                        label={`${u.unique_visitors} user${u.unique_visitors !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                    {u.last_visit && (
                      <Tooltip title={new Date(u.last_visit).toLocaleString()}>
                        <Chip
                          icon={<ScheduleIcon sx={{ fontSize: '0.7rem !important' }} />}
                          label={`last ${new Date(u.last_visit).toLocaleDateString()}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.4 } }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Tooltip title="Copy URL">
                  <IconButton size="small" onClick={() => handleCopyUrl(u.url)} sx={{ flexShrink: 0 }}>
                    <CopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      {/* Footnote about API cap */}
      {!urlsLoading && domainUrls.length > 0 && (
        <Box sx={{ px: 2.5, py: 0.75, borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Showing {filteredUrls.length.toLocaleString()} of {domainUrls.length.toLocaleString()} URLs (max 500 per query)
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderSearchesTab = () => {
    const hasSearches = searchExtractions.length > 0;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Aggregate stats */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1.5,
          }}
        >
          {[
            { label: 'Unique Terms', value: searchAggregates.uniqueTerms.toLocaleString() },
            { label: 'Total Searches', value: searchAggregates.totalSearches.toLocaleString() },
            {
              label: 'Most Recent',
              value: searchAggregates.lastSearch ? searchAggregates.lastSearch.toLocaleString() : '—',
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                p: 1,
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                borderRadius: 1.5,
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {label}
              </Typography>
              <Typography variant="body1" fontWeight={700} sx={{ fontSize: value.length > 18 ? '0.85rem' : '1.05rem', lineHeight: 1.3 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Engine + keyword summary */}
        {hasSearches && (
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
            {searchAggregates.engines.length > 0 && (
              <Box sx={{ mb: topKeywords.length > 0 ? 1 : 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>
                  Engines
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {searchAggregates.engines.map((e) => (
                    <Chip
                      key={e.name}
                      label={`${e.name} · ${e.count}`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        bgcolor: `${e.color}22`,
                        color: e.color,
                        border: `1px solid ${e.color}55`,
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {topKeywords.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>
                  Top Keywords
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {topKeywords.map((k) => (
                    <Chip
                      key={k.word}
                      label={`${k.word} · ${k.count}`}
                      size="small"
                      variant="outlined"
                      onClick={() => setSearchTermFilter(k.word)}
                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Search + sort controls */}
        {hasSearches && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              px: 2.5,
              py: 1.25,
              borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <TextField
              size="small"
              placeholder="Filter terms or engines..."
              value={searchTermFilter}
              onChange={(e) => setSearchTermFilter(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={searchSort}
                label="Sort by"
                onChange={(e) => setSearchSort(e.target.value as SearchSortKey)}
              >
                <MenuItem value="count">Most Searched</MenuItem>
                <MenuItem value="recent">Most Recent</MenuItem>
                <MenuItem value="alpha">A–Z</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Search list */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
          {!hasSearches ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <SearchTermIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">
                No search queries detected for this domain.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Searches are extracted automatically when URLs match a known engine
                (Google, Bing, DuckDuckGo, YouTube, Amazon, eBay, Reddit, X, etc.).
              </Typography>
            </Box>
          ) : visibleSearches.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <Typography variant="body2">No searches match the current filter</Typography>
            </Box>
          ) : (
            <Stack spacing={0.5}>
              {visibleSearches.map((s, idx) => (
                <Paper
                  key={`${s.engine}-${s.term}-${idx}`}
                  elevation={0}
                  sx={{
                    p: 1.25,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                    },
                  }}
                >
                  <SearchTermIcon sx={{ fontSize: 18, mt: 0.25, color: s.engineColor, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.9rem', fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.3 }}
                    >
                      {s.term}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                      <Chip
                        label={s.engine}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          bgcolor: `${s.engineColor}22`,
                          color: s.engineColor,
                          border: `1px solid ${s.engineColor}55`,
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={`${s.count} search${s.count !== 1 ? 'es' : ''}`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                      {s.lastVisit > 0 && (
                        <Tooltip title={new Date(s.lastVisit).toLocaleString()}>
                          <Chip
                            icon={<ScheduleIcon sx={{ fontSize: '0.7rem !important' }} />}
                            label={`last ${new Date(s.lastVisit).toLocaleDateString()}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.4 } }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <Tooltip title="Copy term">
                      <IconButton size="small" onClick={() => handleCopyUrl(s.term)}>
                        <CopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open original search URL">
                      <IconButton size="small" component="a" href={ensureHttps(s.url)} target="_blank" rel="noreferrer">
                        <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    );
  };

  const renderDevicesTab = () => {
    const devices = stats?.connectedDevices || [];
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'primary.main' }}>
          Devices that visited this domain ({devices.length})
        </Typography>

        {devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <DevicesIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">
              No connected devices recorded for this domain in the current view.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {devices.map((d) => (
              <Paper
                key={d}
                elevation={0}
                sx={{
                  p: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                  borderRadius: 1.5,
                }}
              >
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                  <PhoneIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace' }}>
                  {d}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  const renderNotesTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotesIcon sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight="bold">
            Notes ({notes.length})
          </Typography>
        </Box>
        {onAddNote && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddNote}
          >
            Add Note
          </Button>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : notes.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              color: 'text.secondary',
            }}
          >
            <NotesIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">No notes yet</Typography>
            {onAddNote && (
              <Button size="small" sx={{ mt: 1 }} onClick={onAddNote}>
                Add the first note
              </Button>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {notes.map((note) => (
              <Paper
                key={note.id}
                elevation={0}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  borderLeft: `3px solid ${PRIORITY_COLORS[note.priority]}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getPriorityIcon(note.priority)}
                    {note.title && (
                      <Typography variant="subtitle2" fontWeight="bold">
                        {note.title}
                      </Typography>
                    )}
                    <Chip
                      label={PRIORITY_LABELS[note.priority]}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor: `${PRIORITY_COLORS[note.priority]}20`,
                        color: PRIORITY_COLORS[note.priority],
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {onEditNote && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEditNote(note)}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteNote(note.id)}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                  {note.content}
                </Typography>

                {note.tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {note.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{ height: 18, fontSize: '0.6rem' }}
                      />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.secondary' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption">{note.created_by}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption">{formatDate(note.created_at)}</Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDarkMode ? '#1a1a2e' : '#ffffff',
          backgroundImage: 'none',
          maxHeight: '85vh',
          height: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: isDarkMode ? 'primary.dark' : 'primary.main', width: 40, height: 40 }}>
            {getEntityIcon(entityType)}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {entityLabel || entityId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getEntityTypeLabel(entityType)}{websiteDetails ? ` • ${websiteDetails.domain}` : ` • ${entityId}`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tab strip */}
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          px: 1,
          '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: '0.85rem' },
        }}
      >
        <Tab value="overview" label="Overview" icon={<OverviewIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
        {websiteDetails && (
          <Tab
            value="urls"
            label={`URLs${urlsLoading ? '' : ` (${domainUrls.length})`}`}
            icon={<ListAltIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
        )}
        {websiteDetails && searchExtractions.length > 0 && (
          <Tab
            value="searches"
            label={`Searches (${searchExtractions.length})`}
            icon={<SearchTermIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
        )}
        {websiteDetails && (
          <Tab
            value="devices"
            label={`Devices (${stats?.connectedDevices.length ?? 0})`}
            icon={<DevicesIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
        )}
        <Tab
          value="notes"
          label={`Notes (${notes.length})`}
          icon={<NotesIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
      </Tabs>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {tab === 'overview' && renderOverviewTab()}
        {tab === 'urls' && websiteDetails && renderUrlsTab()}
        {tab === 'searches' && websiteDetails && renderSearchesTab()}
        {tab === 'devices' && websiteDetails && renderDevicesTab()}
        {tab === 'notes' && renderNotesTab()}
      </DialogContent>
    </Dialog>
  );
};

export default EntityDetailsDialog;
